import { memo, useMemo } from 'react';
import type {
  Odontogram,
  OdontologyFinding,
  OdontologySpan,
  OdontologyToothStatus,
} from '../../../services/OdontologyController';

interface ToothChange {
  toothId: number;
  removed: string[];
  added: string[];
}

interface OdontogramChangesProps {
  before: Odontogram;
  after: Odontogram;
}

const FINDING_LABELS: Record<string, string> = {
  caries: 'Caries',
  restoration: 'Restauración',
  crown: 'Corona',
  endodontics: 'Endodoncia',
  bridge: 'Puente',
};

const findingDescription = (finding: OdontologyFinding): string => {
  const label = FINDING_LABELS[finding.typeCode] ?? finding.typeCode;
  const surface = finding.surface ? ` ${finding.surface}` : '';
  const icdas = finding.icdas == null ? '' : ` · ICDAS ${finding.icdas}`;
  const state = finding.state === 1 ? 'a realizar' : 'realizado/existente';
  return `${label}${surface}${icdas} (${state})`;
};

const spanDescription = (span: OdontologySpan): string => {
  const label = FINDING_LABELS[span.typeCode] ?? span.typeCode;
  const state = span.state === 1 ? 'a realizar' : 'realizado/existente';
  return `${label} ${span.fromToothFDI}–${span.toToothFDI} (${state})`;
};

const statusDescriptions = (status: OdontologyToothStatus): string[] => {
  const descriptions: string[] = [];
  if (!status.present) descriptions.push('Pieza ausente / extraída');
  if (status.mobility != null) descriptions.push(`Movilidad grado ${status.mobility}`);
  if (status.furcation != null) descriptions.push(`Furcación grado ${status.furcation}`);
  if (status.notes?.trim()) descriptions.push(`Observación: ${status.notes.trim()}`);
  return descriptions;
};

const descriptionsByTooth = (odontogram: Odontogram): Map<number, Set<string>> => {
  const result = new Map<number, Set<string>>();
  const add = (toothId: number, description: string) => {
    const descriptions = result.get(toothId) ?? new Set<string>();
    descriptions.add(description);
    result.set(toothId, descriptions);
  };

  odontogram.findings.forEach((finding) => add(finding.toothFDI, findingDescription(finding)));
  odontogram.spans.forEach((span) => {
    const description = spanDescription(span);
    add(span.fromToothFDI, description);
    add(span.toToothFDI, description);
  });
  odontogram.teeth.forEach((status) => {
    statusDescriptions(status).forEach((description) => add(status.toothFDI, description));
  });
  return result;
};

const calculateChanges = (before: Odontogram, after: Odontogram): ToothChange[] => {
  const previous = descriptionsByTooth(before);
  const current = descriptionsByTooth(after);
  const toothIds = new Set([...previous.keys(), ...current.keys()]);

  return [...toothIds]
    .sort((first, second) => first - second)
    .map((toothId) => {
      const previousDescriptions = previous.get(toothId) ?? new Set<string>();
      const currentDescriptions = current.get(toothId) ?? new Set<string>();
      return {
        toothId,
        removed: [...previousDescriptions].filter((value) => !currentDescriptions.has(value)),
        added: [...currentDescriptions].filter((value) => !previousDescriptions.has(value)),
      };
    })
    .filter((change) => change.removed.length > 0 || change.added.length > 0);
};

function OdontogramChangesComponent({ before, after }: OdontogramChangesProps) {
  const changes = useMemo(() => calculateChanges(before, after), [after, before]);
  if (changes.length === 0) return null;

  return (
    <section className="rounded-xl border border-cyan-200 bg-white p-4 shadow-sm" aria-live="polite">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-700">
            Cambios del control
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Hallazgos anteriores y estado que quedará al guardar
          </p>
        </div>
        <span className="rounded-full bg-cyan-50 px-2.5 py-1 text-xs font-semibold text-cyan-800">
          {changes.length} {changes.length === 1 ? 'pieza modificada' : 'piezas modificadas'}
        </span>
      </div>

      <div className="divide-y divide-slate-100">
        {changes.map((change) => {
          const onlyAdded = change.removed.length === 0;
          const onlyRemoved = change.added.length === 0;
          return (
            <div key={change.toothId} className="grid gap-1 py-2.5 sm:grid-cols-[44px_minmax(0,1fr)]">
              <span className={`text-sm font-bold tabular-nums ${
                onlyAdded ? 'text-emerald-600' : onlyRemoved ? 'text-red-600' : 'text-amber-600'
              }`}>
                {change.toothId}
              </span>
              <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                {onlyAdded ? (
                  <span className="font-medium text-slate-500">Se agregó:</span>
                ) : (
                  <span className="text-slate-400 line-through">
                    {change.removed.join(', ')}
                  </span>
                )}
                {!onlyAdded && !onlyRemoved ? (
                  <i className="pi pi-arrow-right text-[10px] text-cyan-600" aria-hidden="true" />
                ) : null}
                {onlyRemoved ? (
                  <span className="font-medium text-red-600">Se retiró</span>
                ) : (
                  <span className="font-semibold text-slate-700">
                    {change.added.join(', ')}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export const OdontogramChanges = memo(OdontogramChangesComponent);
